import React from 'react';
import { useEntitlements, type PaidModuleKey } from '@/hooks/useEntitlements';
import { useAuth } from '@/contexts/AuthContext';
import { LockedModuleCard } from './LockedModuleCard';

interface Props {
  moduleKey: PaidModuleKey;
  children: React.ReactNode;
  /** Compact locked card (e.g., used inline next to a sidebar item). */
  compact?: boolean;
  /** Override: render this when locked instead of the default locked card. */
  fallback?: React.ReactNode;
}

/**
 * Gates children behind a per-organization module subscription.
 *
 * - Subscribed: renders children.
 * - super_admin without subscription: renders a LockedModuleCard with upsell.
 * - Everyone else without subscription: renders null (fully hidden).
 */
export const ModuleGate: React.FC<Props> = ({
  moduleKey,
  children,
  compact,
  fallback,
}) => {
  const { user } = useAuth();
  const { hasModule, getModule, isLoading } = useEntitlements();

  if (isLoading) return null;
  if (hasModule(moduleKey)) return <>{children}</>;

  if (user?.role !== 'super_admin') return null;

  const mod = getModule(moduleKey);
  if (!mod) return null;
  if (fallback) return <>{fallback}</>;

  return <LockedModuleCard module={mod} compact={compact} />;
};

export default ModuleGate;
